// ============================================================================
// TOASTCONTEXT.JS - CONTEXTO GLOBAL PARA SISTEMA DE NOTIFICACIONES TOAST
// ============================================================================

import React, { createContext, useContext, useState, useCallback } from 'react';

// Crear el contexto
const ToastContext = createContext();

// Hook para usar el contexto
export const useToastContext = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToastContext debe usarse dentro de ToastProvider');
  }
  return context;
};

// Configuración por defecto
const DEFAULT_CONFIG = {
  duration: 4000,        // 4 segundos por defecto
  position: 'top-right', // Posición por defecto
  maxToasts: 5,         // Máximo 5 toasts simultáneos
};

// Tipos de toast disponibles
export const TOAST_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info'
};

// Proveedor del contexto
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  // ============================================================================
  // FUNCIONES PRINCIPALES
  // ============================================================================

  // Generar ID único para cada toast
  const generateId = useCallback(() => {
    return Date.now() + Math.random().toString(36).substring(2);
  }, []);

  // Función principal para agregar toast
  const addToast = useCallback((message, type = TOAST_TYPES.INFO, options = {}) => {
    const id = generateId();
    const config = { ...DEFAULT_CONFIG, ...options };

    const newToast = {
      id,
      message,
      type,
      timestamp: Date.now(),
      duration: config.duration,
      dismissible: config.dismissible !== false, // Por defecto es true
      // Título/descripción opcionales: si no se pasan, `message` hace de título
      // (retrocompat con todas las llamadas existentes de una sola línea).
      title: config.title || null,
      description: config.description || null,
      // Acción opcional (p.ej. "Deshacer"): { label, onClick }. Se pinta como
      // botón dentro del toast; al pulsarlo se ejecuta onClick y se cierra.
      action: config.action || null,
    };

    setToasts(prevToasts => {
      // Limitar número máximo de toasts
      const updatedToasts = [...prevToasts, newToast];

      if (updatedToasts.length > config.maxToasts) {
        // Eliminar los más antiguos PERO nunca un 'confirm': tiene duration:0 y
        // una Promise pendiente; si se expulsa, el await de showConfirm queda
        // colgado para siempre (deadlock del flujo que esperaba la decisión).
        const confirms = updatedToasts.filter(t => t.type === 'confirm');
        const rest = updatedToasts.filter(t => t.type !== 'confirm');
        const keepRest = rest.slice(-Math.max(0, config.maxToasts - confirms.length));
        return [...confirms, ...keepRest];
      }

      return updatedToasts;
    });

    // Auto-dismiss si tiene duración
    if (config.duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, config.duration);
    }

    return id;
  }, [generateId]);

  // Eliminar toast específico
  const removeToast = useCallback((id) => {
    setToasts(prevToasts => prevToasts.filter(toast => toast.id !== id));
  }, []);

  // Limpiar todos los toasts
  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  // ============================================================================
  // FUNCIONES DE CONVENIENCIA POR TIPO
  // ============================================================================

  const showSuccess = useCallback((message, options = {}) => {
    return addToast(message, TOAST_TYPES.SUCCESS, {
      duration: 3000, // Éxito se muestra menos tiempo
      ...options
    });
  }, [addToast]);

  const showError = useCallback((message, options = {}) => {
    return addToast(message, TOAST_TYPES.ERROR, {
      duration: 6000, // Error se muestra más tiempo
      ...options
    });
  }, [addToast]);

  const showWarning = useCallback((message, options = {}) => {
    return addToast(message, TOAST_TYPES.WARNING, {
      duration: 5000, // Warning duración media
      ...options
    });
  }, [addToast]);

  const showInfo = useCallback((message, options = {}) => {
    return addToast(message, TOAST_TYPES.INFO, {
      duration: 4000, // Info duración normal
      ...options
    });
  }, [addToast]);

  // ============================================================================
  // FUNCIONES DE CONFIRMACIÓN (REEMPLAZA window.confirm)
  // ============================================================================

  const showConfirm = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      const id = generateId();
      
      const confirmToast = {
        id,
        message,
        type: 'confirm',
        timestamp: Date.now(),
        duration: 0, // No auto-dismiss
        dismissible: false, // No se puede cerrar sin decidir
        onConfirm: () => {
          removeToast(id);
          resolve(true);
        },
        onCancel: () => {
          removeToast(id);
          resolve(false);
        }
      };

      setToasts(prevToasts => [...prevToasts, confirmToast]);
    });
  }, [generateId, removeToast]);

  // ============================================================================
  // FUNCIONES UTILITARIAS
  // ============================================================================

  // Actualizar toast existente
  const updateToast = useCallback((id, updates) => {
    setToasts(prevToasts => 
      prevToasts.map(toast => 
        toast.id === id 
          ? { ...toast, ...updates }
          : toast
      )
    );
  }, []);

  // Obtener toast por ID
  const getToast = useCallback((id) => {
    return toasts.find(toast => toast.id === id);
  }, [toasts]);

  // Contar toasts por tipo
  const getToastCount = useCallback((type = null) => {
    if (!type) return toasts.length;
    return toasts.filter(toast => toast.type === type).length;
  }, [toasts]);

  // ============================================================================
  // VALOR DEL CONTEXTO
  // ============================================================================

  const contextValue = {
    // Estado
    toasts,
    
    // Funciones principales
    addToast,
    removeToast,
    clearAllToasts,
    updateToast,
    
    // Funciones por tipo
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showConfirm,
    
    // Utilitarias
    getToast,
    getToastCount,
    
    // Constantes
    TOAST_TYPES,
    DEFAULT_CONFIG
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
    </ToastContext.Provider>
  );
}